import * as Sentry from '@sentry/nextjs';
import { safeOrderPathContext } from './error-context';

/**
 * Meld een AFGEVANGEN fout op het bestelpad.
 *
 * Waarom dit bestaat: een mislukte opslag in de editor werd afgevangen met
 * `console.warn` en een toast bij de klant. Daardoor was een fout die
 * bestellingen blokkeerde volledig onzichtbaar - geen Sentry, geen logregel,
 * geen alert. De eigenaar was het monitoringsysteem. Op 1-8-2026 kostte dat
 * omzet: Vercel weigerde elke upload boven ~4,5 MB met een 413 en niemand
 * kon dat zien.
 *
 * Gebruik dit overal waar een `catch` een handeling afbreekt die de klant
 * nodig heeft om te bestellen. Niet voor verwachte validatie ("kies eerst een
 * maat"), wel voor alles wat kapot is.
 */
export type OrderPathStep =
  | 'design-save'
  | 'design-upload'
  | 'file-upload'
  | 'print-file-generate'
  | 'price-fetch'
  | 'cart-add'
  | 'checkout-start'
  // Aanmaken of tellen van een editor-sessie. Eigen label, anders landt een
  // storing rond sessies onder 'file-upload' en lijkt het een uploadprobleem.
  | 'editor-session'
  // De ontwerpeditor zelf: vastlopers en overgeslagen zware werkbestanden.
  | 'editor-open'
  | 'marketing-event'
  // Server-side: een BETAALDE order kon niet bij een leverancier terecht. Zelfde
  // gedachte als hierboven, alleen ligt de schade later in de keten: het geld
  // is binnen en het drukwerk is niet begonnen. Serverfouten op dit stuk werden
  // allemaal afgevangen met console.error en bereikten Sentry dus nooit; zie
  // src/lib/fulfilment/hold.ts.
  | 'provider-submit'
  // Interne snelheidsbegrenzing op het partner-order-endpoint (bv.
  // portretpet). Geen klantpad, maar de storing hoort in dezelfde
  // Sentry-stroom als de rest van het bestelpad.
  | 'partner-order';

type Context = {
  /** productslug of id, zodat een fout op één product opvalt */
  product?: string;
  /** bestandsgrootte in bytes, de aanleiding voor deze module */
  bytes?: number;
  /** HTTP-status als die er is; 413 betekent platformgrens, niet applicatie */
  status?: number;
  /** Known code only; duration must be measured by the caller, never inferred. */
  code?: string;
  durationMs?: number;
  /** Legacy callers remain accepted; only allowlisted context is reported. */
  [key: string]: unknown;
};

function toError(value: unknown): Error {
  if (value instanceof Error) return value;
  return new Error(typeof value === 'string' ? value : JSON.stringify(value));
}

/**
 * Meldt naar Sentry met een tag per stap, zodat je in Sentry kunt filteren op
 * `order_path_step:design-save` en meteen ziet of het één klant is of iedereen.
 * Faalt nooit: monitoring mag de handeling zelf niet omver trekken.
 */
export function reportOrderPathFailure(step: OrderPathStep, error: unknown, context: Context = {}) {
  try {
    const err = toError(error);
    const extra = safeOrderPathContext(context);
    console.error(`[order-path] ${step} failed`, err, extra);
    Sentry.captureException(err, {
      level: 'error',
      tags: { order_path_step: step, ...(extra.status ? { http_status: String(extra.status) } : {}),
        ...(extra.code ? { error_code: String(extra.code) } : {}),
        ...(extra.release ? { app_release: String(extra.release) } : {}),
        ...(extra.browserPlatform ? { browser_platform: String(extra.browserPlatform) } : {}) },
      extra,
    });
  } catch {
    // Als melden zelf stukgaat is dat geen reden om de klant te storen.
  }
}

/**
 * Voor het geval dat er geen Error is maar wel iets misging dat je wilt zien,
 * bijvoorbeeld een respons die technisch klopt maar leeg terugkomt.
 */
export function reportOrderPathWarning(step: OrderPathStep, message: string, context: Context = {}) {
  try {
    const extra = safeOrderPathContext(context);
    console.warn(`[order-path] ${step}: ${message}`, extra);
    Sentry.captureMessage(`[order-path] ${step}: ${message}`, {
      level: 'warning',
      tags: { order_path_step: step, ...(extra.code ? { error_code: String(extra.code) } : {}),
        ...(extra.release ? { app_release: String(extra.release) } : {}),
        ...(extra.browserPlatform ? { browser_platform: String(extra.browserPlatform) } : {}) },
      extra,
    });
  } catch {
    // idem
  }
}
